/**
 * Header Nav — ESM migration of header-nav.js (Phase 11).
 *
 * Manages desktop nested-menu hover/reveal, mobile nav open/close, veil, and
 * spotlight-link hiding. init() runs once on ep:ready. Because #header-global is
 * data-turbo-permanent, Turbo carries the header node and its bound listeners
 * across navigations, so there is no turbo:load re-init — listeners attach once
 * and survive every nav.
 *
 * The header no longer auto-hides on scroll; the scroll-driven logo→mark
 * crossfade is handled by `header-scrolled` (set in application.esm.js) plus CSS
 * in _header.scss, so no show/hide animation lives here.
 *
 * trapFocus is called via safeAppCall because app.trapFocus is part of
 * the late-hydrated `app` namespace (mostly mobile-nav timing).
 *
 * AI: Entirely written by AI
 * Date: 2026-05-07
 * Model: Claude Sonnet 4.6
 * Updated: 2026-06-02 (Claude Opus 4.8) - #header-global data-turbo-permanent; drop turbo:load re-init
 * Updated: 2026-06-09 (Claude Opus 4.8) - centered-logo redesign; remove scroll auto-hide (logo↔mark crossfade)
 * Updated: 2026-06-09 (Claude Opus 4.8) - mobile nav slides in from the left over content (no page push)
 *
 * [HeaderNav]
 */

import { log } from './log.js';
import { appGet, safeAppCall } from './app-get.js';
import { fadeIn, fadeOut, stop } from './core-anim.js';
import { saveLastFocused, restoreLastFocused } from './overlay.js';

const headerSelector = '#header-global';
const mobileNavSelector = '#mobile-nav';
const veilSelector = '#nav-veil';
const navIconSelector = '.js-mobile-nav-icon';
const menuVisibleClass = 'menu-visible';
const nestedMenuOpenClass = 'nested-menu-open';
const navLinkSelector = 'a.top-nav-link';
const hasNestedLinksSelector = '.header-nav-li.has-nested-links';
const nestedLinksSelector = headerSelector + ' ' + hasNestedLinksSelector;
const mobileNavDuration = 300;
const mobileNavOpenClass = 'mobile-nav-open';

let easing;
let canShowNestedMenu;

// Handlers keyed by element so we can remove/re-add them on link-click transitions.
const nestedMenuHandlers = new Map();

function init() {
  easing = appGet('easing');
  canShowNestedMenu = appGet('currentBreakpoint') !== 'small' && appGet('currentBreakpoint') !== 'medium';

  const hasSpotlightLink = document.querySelectorAll('.js-spotlight-link').length > 0;
  if (hasSpotlightLink) {
    handleSpotlightLink(null, appGet('url.path'));
    window.app.observer('url.path', handleSpotlightLink);
  }

  window.app.observer('currentBreakpoint', (event, value) => {
    canShowNestedMenu = value !== 'small' && value !== 'medium';
    if (document.documentElement.classList.contains(mobileNavOpenClass) && canShowNestedMenu) {
      closeMobileNav();
    }
  });

  // #header-global is data-turbo-permanent, so the header node (and these
  // listeners) survives every Turbo navigation — attach mouseenter/mouseleave
  // directly to each nested-menu item once at init; no re-attach needed.
  attachNestedMenuListeners();

  document.addEventListener('click', (e) => {
    if (e.target.closest(navIconSelector)) {
      handleNavIconClick(e);
    }
  });

  // turbo:before-visit fires before every Turbo Drive navigation (replaces
  // page:beforechange). The header is data-turbo-permanent, so the body swap no
  // longer implicitly tears down open menus — close any open mobile nav AND any
  // open desktop dropdown before navigating so nothing carries over.
  document.addEventListener('turbo:before-visit', () => {
    handleMobileNavOnChange();
    resetOpenDropdownsOnChange();
  });

  log('[HeaderNav] initialized');
}

// ─── Nested Menu Listener Management ────────────────────────────────────────

function attachNestedMenuListeners() {
  document.querySelectorAll(nestedLinksSelector).forEach((item) => {
    const onEnter = () => handleMouseEnter(item);
    const onLeave = () => handleMouseLeave(item);
    nestedMenuHandlers.set(item, { onEnter, onLeave });
    item.addEventListener('mouseenter', onEnter);
    item.addEventListener('mouseleave', onLeave);
  });
}

function detachNestedMenuListeners() {
  nestedMenuHandlers.forEach(({ onEnter, onLeave }, item) => {
    item.removeEventListener('mouseenter', onEnter);
    item.removeEventListener('mouseleave', onLeave);
  });
}

// Idempotent: removes any currently-mapped listeners before re-querying and
// re-binding. Safe to call repeatedly and from overlapping triggers (the
// fadeOut callback after a dropdown-link click can race turbo:load) — each call
// fully resets to exactly one listener set on the live nav nodes. After a Turbo
// navigation the mapped nodes are the discarded old header (removeEventListener
// is a harmless no-op on detached nodes); attach then binds the new header.
function reattachNestedMenuListeners() {
  detachNestedMenuListeners();
  nestedMenuHandlers.clear();
  attachNestedMenuListeners();
}

// ─── Nested Menus ──────────────────────────────────────────────────────────

function handleMouseEnter(item) {
  if (!canShowNestedMenu) return;
  const menu = item.querySelector('.js-nav-menu-dropdown');
  if (item.classList.contains(menuVisibleClass)) return;

  item.classList.add(menuVisibleClass);
  item.querySelector(navLinkSelector)?.setAttribute('aria-expanded', 'true');
  stop(menu);
  fadeIn(menu, { duration: 300 });

  const html = document.documentElement;
  if (!html.classList.contains(nestedMenuOpenClass)) {
    html.classList.add(nestedMenuOpenClass);
    showVeil();
  }

  // Temporarily suppress hover events while the menu closes after a link click
  // so the menu doesn't immediately re-open before the navigation resolves.
  item.querySelectorAll('a, [data-href]').forEach((el) => {
    el.addEventListener(
      'click',
      () => {
        detachNestedMenuListeners();
        handleMouseLeave(item, reattachNestedMenuListeners);
      },
      { once: true }
    );
  });
}

function handleMouseLeave(item, callback) {
  if (!canShowNestedMenu) return;
  const menu = item.querySelector('.js-nav-menu-dropdown');
  item.classList.remove(menuVisibleClass);
  item.querySelector(navLinkSelector)?.setAttribute('aria-expanded', 'false');
  item.style.pointerEvents = '';
  stop(menu);
  fadeOut(menu, { duration: 300 }).then(() => {
    if (typeof callback === 'function') callback();
  });
  setTimeout(() => {
    const header = document.querySelector(headerSelector);
    if (header && header.querySelectorAll('.' + menuVisibleClass).length === 0) {
      document.documentElement.classList.remove(nestedMenuOpenClass);
      hideVeil();
    }
  }, 50);
}

// ─── Mobile Nav ────────────────────────────────────────────────────────────

function handleMobileNavOnChange() {
  if (document.documentElement.classList.contains(mobileNavOpenClass)) {
    closeMobileNav();
  }
}

// The header is data-turbo-permanent, so an open desktop dropdown (and its
// veil / nested-menu-open state / inline fade styles) would otherwise persist
// into the next page. Close any open dropdown before a Turbo navigation —
// replacing the cleanup the body swap used to provide for free.
function resetOpenDropdownsOnChange() {
  const header = document.querySelector(headerSelector);
  if (header) {
    header.querySelectorAll('.' + menuVisibleClass).forEach((item) => {
      item.classList.remove(menuVisibleClass);
      item.querySelector(navLinkSelector)?.setAttribute('aria-expanded', 'false');
      item.style.pointerEvents = '';
      const menu = item.querySelector('.js-nav-menu-dropdown');
      if (menu) {
        stop(menu);
        menu.removeAttribute('style');
      }
    });
  }
  document.documentElement.classList.remove(nestedMenuOpenClass);
  hideVeil();
}

function handleNavIconClick(event) {
  event.preventDefault();
  const isMobileNavOpen = document.documentElement.classList.contains(mobileNavOpenClass);
  if (!isMobileNavOpen) {
    openMobileNav();
  } else {
    closeMobileNav();
  }
}

function openMobileNav() {
  const html = document.documentElement;
  const mobileNav = document.querySelector(mobileNavSelector);
  const veil = document.querySelector(veilSelector);
  const navIcon = document.querySelector(navIconSelector);

  // Position nav off-screen to the left, then make it visible. The nav slides
  // in OVER the page (the veil dims it) — the page itself is no longer
  // translated, so content stays put behind the drawer.
  mobileNav.style.display = 'block';
  mobileNav.style.transform = 'translateX(-100%)';

  showVeil();
  saveLastFocused();
  navIcon?.setAttribute('aria-expanded', 'true');

  mobileNav
    .animate(
      [{ transform: 'translateX(-100%)' }, { transform: 'translateX(0)' }],
      { duration: mobileNavDuration, easing, fill: 'forwards' }
    )
    .finished.then(() => {
      safeAppCall('trapFocus', mobileNav);
      const firstVisible = Array.from(mobileNav.querySelectorAll('button')).find(
        (el) => el.offsetParent !== null
      );
      firstVisible?.focus();
      veil.setAttribute('tabindex', '0');
    });

  window.app.set('isScrollPrevented', true);
  html.classList.add(mobileNavOpenClass);

  veil.addEventListener(
    'click',
    () => {
      if (html.classList.contains(mobileNavOpenClass)) closeMobileNav();
    },
    { once: true }
  );
}

function closeMobileNav() {
  const html = document.documentElement;
  const mobileNav = document.querySelector(mobileNavSelector);
  const veil = document.querySelector(veilSelector);

  hideVeil();
  mobileNav
    .animate(
      [{ transform: 'translateX(0)' }, { transform: 'translateX(-100%)' }],
      { duration: mobileNavDuration, easing, fill: 'forwards' }
    )
    .finished.then(() => {
      veil.removeAttribute('tabindex');
      restoreLastFocused();
    });

  window.app.set('isScrollPrevented', false);
  html.classList.remove(mobileNavOpenClass);

  // Let any open <nav-panel> reset to the top level while the drawer is closed.
  document.dispatchEvent(new CustomEvent('mobile-nav:close'));
}

// ─── Veil ──────────────────────────────────────────────────────────────────

function showVeil() {
  const veil = document.querySelector(veilSelector);
  stop(veil);
  fadeIn(veil, { duration: 300 });
}

function hideVeil() {
  const veil = document.querySelector(veilSelector);
  stop(veil);
  fadeOut(veil, { duration: 300 });
}

// ─── Spotlight Link ────────────────────────────────────────────────────────

function handleSpotlightLink(event, value) {
  const shouldAnimate = event != null && event.type && event.type.includes('propchange');
  const isLargeUp = appGet('currentBreakpoint') !== 'small' && appGet('currentBreakpoint') !== 'medium';
  const spotlightLink = isLargeUp
    ? document.querySelector('.js-spotlight-link-lg')
    : document.querySelector('.js-spotlight-link-sm');
  const linkUrl = spotlightLink?.getAttribute('href');
  const hiddenClass = 'spotlight-link-hidden';

  if (value === linkUrl) {
    if (shouldAnimate && spotlightLink) {
      fadeOut(spotlightLink, { duration: 300 }).then(() => {
        spotlightLink.removeAttribute('style');
      });
    }
    document.body.classList.add(hiddenClass);
  } else if (spotlightLink && getComputedStyle(spotlightLink).display === 'none') {
    if (shouldAnimate) {
      spotlightLink.style.display = 'inline-block';
      fadeIn(spotlightLink, { duration: 300, display: 'inline-block' }).then(() => {
        spotlightLink.removeAttribute('style');
      });
    }
    document.body.classList.remove(hiddenClass);
  }
}

// ─── Bootstrap ─────────────────────────────────────────────────────────────

// #header-global is now data-turbo-permanent, so Turbo transplants the header
// node — with its bound mouseenter/mouseleave listeners — into each new page
// instead of replacing it. init() therefore runs exactly once (on ep:ready),
// attaches the nested-menu listeners once, and they ride the transplant across
// every navigation. No turbo:load re-attach is needed (it would just tear down
// and rebind the same surviving nodes for no reason).
//
// detachNestedMenuListeners / reattachNestedMenuListeners remain for the
// link-click suppression path (see handleMouseEnter): on the persistent <li>
// they correctly detach-on-click then reattach after the close animation.
document.addEventListener('ep:ready', init, { once: true });

log('[HeaderNav] loaded');
